import {FIELD_BIOME_LAYER_KEYS, FIELD_BIOME_TEXTURES} from '../../../configs/fieldBiomeEffects';
import {SNOW_DRIFT_KIND} from '../../../configs/biomeEffects/snowDrift';
import {resolveSpawnBox} from './biomeContext';
import {
  TWO_PI,
  pickPointInBox,
  randomIn,
  randomInt,
  resolveOvalFade,
  smoothstep,
  writeHiddenPose,
  writePose,
} from './biomeMath';
import {SNOW_SQUALL_TEXTURES, createSnowSquallTracks} from './snowSquall';

/**
 * ПОЗЕМКА И ВЬЮГА (зимние подложки). Чистые функции без Phaser и DOM: по этому
 * описанию частицы каждый кадр ставит сцена (scenes/FieldBiomeEffects).
 *
 * ЛЕНТА СНЕГА — цепочка коротких тонких штрихов, уложенных внахлест вдоль
 * своего русла. Русло — две волны поперек пути, НЕПОДВИЖНЫЕ в пространстве:
 * место штриха поперек пути зависит только от того, где он сейчас вдоль пути.
 * Поэтому лента не едет жесткой загогулиной, а течет по извилистой дорожке,
 * как дымок по неровной земле, и каждый штрих лежит по касательной к ней.
 * Летит лента по ветру матча (тому же, что несет облака).
 *
 * Главная волна у лент одного порыва ОБЩАЯ (длина и место в пространстве;
 * размах у каждой ленты свой, гребни соседних чуть сдвинуты): ленты изгибаются
 * согласно, как струи одного потока, и друг друга не пересекают. Вторая,
 * мелкая волна у каждой ленты своя.
 *
 * Ленты стелются по жетонам строений и гор, но под отрядами и их значками
 * (слой LOW_AIR).
 *
 * Дорожки:
 *  - порывы: полоса из двух с лишним десятков лент проходит часть поля там, куда
 *    смотрит камера. У каждой ленты свое отставание от фронта: порыв идет
 *    россыпью, а не шеренгой;
 *  - одиночные ленты между порывами;
 *  - вьюга: время от времени — снежный заряд (./snowSquall).
 *
 * Ленты видны только на чистом снегу подложки: к краю снежной равнины гаснут —
 * на лесах, реках, скалах и воде их нет. В начале и в конце своего пути лента
 * проступает и тает.
 *
 * Дорожка порывов помечена (SNOW_DRIFT_GUST_TAG): по метке сцена находит идущий
 * порыв и, когда он в кадре игрока, играет свист ветра (см.
 * scenes/FieldEffectSounds). Рождаться порывам метка не мешает: сцена их не
 * придерживает. Одиночные ленты между порывами — без метки: они молчат.
 */

/** Метка дорожки порывов поземки: по ней сцена находит идущий порыв */
export const SNOW_DRIFT_GUST_TAG = 'snowDriftGust';

/** Плотность вдоль ленты: 0 — хвост, 1 — голова; концы сходят на нет, голова плотнее */
const resolveBodyProfile = (bodyShare, {taperPower, tailShare}) => (
  Math.sin(Math.PI * bodyShare) ** taperPower * (tailShare + (1 - tailShare) * bodyShare)
);

const createTracks = (context, params) => {
  const {hexSize, wind} = context;
  const {gusts, lone, ribbon} = params;
  const across = {x: -wind.y, y: wind.x};
  const snow = context.toWorldOval({u: params.snow.u, v: params.snow.v, radiusU: params.snow.fadeRadius});
  // Какая доля радиуса уходит на спад: от кромки чистого снега до нуля
  const snowFade = 1 - params.snow.cleanRadius / params.snow.fadeRadius;
  /** Есть ли в точке снег, по которому идет поземка */
  const checkIfOnSnow = ({x, y}) => resolveOvalFade(snow, x, y, snowFade) > 0;

  /** Отклонение русла ленты поперек пути в данном месте пути: две волны, неподвижные в пространстве */
  const resolveLaneOffset = (particle, along) => (
    particle.lane
    + particle.waveAmplitude * Math.sin((TWO_PI * along) / particle.waveLength + particle.wavePhase)
    + particle.rippleAmplitude * Math.sin((TWO_PI * along) / particle.rippleLength + particle.ripplePhase)
  );
  /** Наклон русла к пути в данном месте: производная отклонения */
  const resolveLaneSlope = (particle, along) => (
    particle.waveAmplitude * (TWO_PI / particle.waveLength)
      * Math.cos((TWO_PI * along) / particle.waveLength + particle.wavePhase)
    + particle.rippleAmplitude * (TWO_PI / particle.rippleLength)
      * Math.cos((TWO_PI * along) / particle.rippleLength + particle.ripplePhase)
  );

  /**
   * Главная волна русла, общая для лент одной группы: ленты идут как струи
   * одного потока
   */
  const createFlowWave = (rng) => ({
    amplitude: hexSize * randomIn(rng, ribbon.waveAmplitudeFactor),
    length: hexSize * randomIn(rng, ribbon.waveLengthFactor),
    phase: rng() * TWO_PI,
    // Сдвиг гребня волны на единицу расстояния поперек ветра
    shear: randomIn(rng, ribbon.wavePhaseShear) / hexSize,
  });

  /**
   * Лента: штрихи от хвоста к голове с общими числами русла.
   * @param {Object} flow — главная волна русла (createFlowWave)
   * @param {Object} place — {lane, lag, speed}: дорожка поперек пути,
   *   отставание от фронта и скорость
   */
  const createRibbonParticles = (rng, flow, place) => {
    const length = hexSize * randomIn(rng, ribbon.lengthFactor);
    const thickness = hexSize * randomIn(rng, ribbon.thicknessFactor);
    const alpha = params.white.alpha * randomIn(rng, ribbon.alphaShare);
    const waveAmplitude = flow.amplitude * randomIn(rng, ribbon.waveAmplitudeShare);
    const waveLength = flow.length;
    const base = {
      layer: FIELD_BIOME_LAYER_KEYS.LOW_AIR,
      texture: FIELD_BIOME_TEXTURES.SNOW_STREAK,
      tint: params.white.tint,
      ...place,
      length,
      segmentLength: (length / ribbon.segmentCount) * ribbon.segmentOverlap,
      waveAmplitude,
      waveLength,
      wavePhase: flow.phase + flow.shear * place.lane,
      rippleAmplitude: waveAmplitude * randomIn(rng, ribbon.rippleAmplitudeShare),
      rippleLength: waveLength * randomIn(rng, ribbon.rippleLengthShare),
      ripplePhase: rng() * TWO_PI,
      pulsePeriodMs: randomIn(rng, ribbon.pulsePeriodMs),
      pulsePhase: rng() * TWO_PI,
    };
    const particles = [];
    for (let segment = 0; segment < ribbon.segmentCount; segment += 1) {
      // Место штриха в ленте: 0 — хвост, 1 — голова
      const bodyShare = (segment + 0.5) / ribbon.segmentCount;
      particles.push({
        ...base,
        bodyShare,
        // Плотность и толщина штриха на его месте в ленте: к хвосту лента бледнее и тоньше
        segmentAlpha: alpha * resolveBodyProfile(bodyShare, ribbon),
        segmentThickness: thickness * (ribbon.tailThicknessShare + (1 - ribbon.tailThicknessShare) * bodyShare),
      });
    }
    return particles;
  };

  /**
   * Поза штриха ленты. Путь группы: от `from` до `to` вдоль ветра через точку
   * `through`; на отрезках `fade` в начале и в конце пути лента проступает и тает
   */
  const resolveRibbonPose = (group, particle, elapsedMs, out) => {
    if (!(elapsedMs > 0) || !(elapsedMs < group.durationMs)) {
      return writeHiddenPose(out);
    }
    // Голова ленты идет от начала пути, штрих — на своем месте позади нее
    const headAlong = group.from - particle.lag + (particle.speed * elapsedMs) / 1000;
    const along = headAlong - (1 - particle.bodyShare) * particle.length;
    const pathFade = Math.min(
      smoothstep(group.from, group.from + group.fade, along), smoothstep(group.to, group.to - group.fade, along)
    );
    if (!(pathFade > 0)) {
      return writeHiddenPose(out);
    }
    // Русло неподвижно: отклонение поперек пути — функция места вдоль пути
    const offset = resolveLaneOffset(particle, along);
    const x = group.through.x + wind.x * along + across.x * offset;
    const y = group.through.y + wind.y * along + across.y * offset;
    const ground = resolveOvalFade(snow, x, y, snowFade);
    if (!(ground > 0)) {
      return writeHiddenPose(out);
    }

    const pulse = 1 - ribbon.pulseDepth
      * (0.5 + 0.5 * Math.sin((TWO_PI * elapsedMs) / particle.pulsePeriodMs + particle.pulsePhase));
    // Штрих лежит по касательной к руслу. Вверх ногами его не кладут: тень
    // нарисована в самом штрихе и должна остаться под гребнем, куда бы ни дул
    // ветер, — направление берется «слева направо»
    const slope = resolveLaneSlope(particle, along);
    const tangentX = wind.x + across.x * slope;
    const tangentY = wind.y + across.y * slope;
    const facing = tangentX < 0 ? -1 : 1;
    return writePose(
      out, x, y, particle.segmentAlpha * pulse * pathFade * ground, particle.segmentLength, particle.segmentThickness,
      Math.atan2(tangentY * facing, tangentX * facing)
    );
  };

  /** Срок группы: пока хвост самой отставшей ленты не дойдет до конца пути */
  const resolveDurationMs = (particles, from, to) => Math.max(
    ...particles.map(({lag, length, speed}) => ((to - from + lag + length) / speed) * 1000)
  );

  // --- порывы

  const buildGust = ({rng, view}) => {
    // Середина пути порыва — там, куда смотрит камера
    const through = pickPointInBox(rng, resolveSpawnBox(context, view, {fieldMarginFactor: 0, viewMarginFactor: 0}));
    // Камера смотрит за край снежной равнины: порыв прошел бы невидимым —
    // очередь пропускается, следующая попытка возьмет другую точку
    if (!checkIfOnSnow(through)) {
      return null;
    }
    const travel = hexSize * randomIn(rng, gusts.travelFactor);
    const speed = hexSize * randomIn(rng, gusts.speedFactor);
    const count = randomInt(rng, gusts.count.min, gusts.count.max);
    // Дорожки лент поперек ветра: соседние — на случайном расстоянии друг от друга
    const lanes = [0];
    for (let index = 1; index < count; index += 1) {
      lanes.push(lanes[index - 1] + hexSize * randomIn(rng, gusts.laneGapFactor));
    }
    const bandWidth = lanes[count - 1];

    const flow = createFlowWave(rng);
    const particles = [];
    for (let index = 0; index < count; index += 1) {
      particles.push(...createRibbonParticles(rng, flow, {
        ribbonIndex: index,
        lane: lanes[index] - bandWidth / 2,
        lag: hexSize * randomIn(rng, gusts.lagFactor),
        speed: speed * randomIn(rng, ribbon.speedShare),
      }));
    }
    const from = -travel / 2;
    const to = travel / 2;
    return {
      durationMs: resolveDurationMs(particles, from, to), through, from, to, fade: hexSize * gusts.fadeFactor, particles,
    };
  };

  // --- одиночные ленты

  const buildLone = ({rng, view}) => {
    // Середина пути ленты — там, куда смотрит камера
    const through = pickPointInBox(rng, resolveSpawnBox(context, view, {fieldMarginFactor: 1, viewMarginFactor: 1}));
    // За краем снежной равнины ленте взяться неоткуда
    if (!checkIfOnSnow(through)) {
      return null;
    }
    const travel = hexSize * randomIn(rng, lone.travelFactor);
    const particles = createRibbonParticles(rng, createFlowWave(rng), {
      ribbonIndex: 0, lane: 0, lag: 0, speed: hexSize * randomIn(rng, lone.speedFactor),
    });
    const from = -travel / 2;
    const to = travel / 2;
    return {
      durationMs: resolveDurationMs(particles, from, to), through, from, to, fade: hexSize * lone.fadeFactor, particles,
    };
  };

  return [
    {
      tag: SNOW_DRIFT_GUST_TAG,
      schedule: {firstDelayMs: gusts.firstDelayMs, intervalMs: gusts.intervalMs, maxGroups: 1},
      buildGroup: buildGust,
      resolvePose: resolveRibbonPose,
    },
    {
      schedule: {
        firstDelayMs: lone.firstDelayMs, intervalMs: lone.intervalMs, maxGroups: lone.maxGroups, prewarm: lone.prewarm,
      },
      buildGroup: buildLone,
      resolvePose: resolveRibbonPose,
    },
    ...createSnowSquallTracks(context, params.squall, params.squallTint),
  ];
};

const snowDrift = {
  kind: SNOW_DRIFT_KIND,
  textures: [...new Set([FIELD_BIOME_TEXTURES.SNOW_STREAK, ...SNOW_SQUALL_TEXTURES])],
  createTracks,
};

export default snowDrift;
